import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Database, RefreshCw, CheckCircle2, AlertTriangle, Cloud, Play,
  Activity, Clock,
} from 'lucide-react';

/**
 * BackupPage — Admin dashboard for the Atlas MongoDB backup worker
 * (iter 130). Shows connection health, last sync summary, per-collection
 * progress, and a "Run backup now" trigger button.
 */
export default function BackupPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/backup/status');
      setState(r.data);
    } catch (e) {
      toast.error('Failed to load backup status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 8s so the UI reflects the background worker's progress live.
    const t = setInterval(fetchStatus, 8000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const testConnection = async () => {
    setPinging(true);
    try {
      const r = await api.post('/api/admin/backup/test-connection');
      if (r.data?.ok) {
        toast.success(`Atlas reachable → ${r.data.db}`);
      } else {
        toast.error(r.data?.error || 'Ping failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ping failed');
    } finally {
      setPinging(false);
      fetchStatus();
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/api/admin/backup/run-now');
      if (r.data?.ok) {
        toast.success('Backup started — watch the per-collection progress below');
      } else {
        toast.error(r.data?.error || 'Could not start backup');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup trigger failed');
    } finally {
      setRunning(false);
      setTimeout(fetchStatus, 500);
    }
  };

  if (loading || !state) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-400" data-testid="backup-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading backup status…
      </div>
    );
  }

  const isHealthy = state.last_status === 'ok' || state.last_status === 'pinged';
  const isRunningLive = state.running === true;
  const statusColor = isHealthy
    ? 'text-green-400'
    : state.last_status === 'error'
      ? 'text-red-400'
      : 'text-amber-400';

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-backup-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cloud className="w-6 h-6 text-sky-400" />
            MongoDB Atlas Backup
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Mirrors every collection to a remote Atlas cluster so that a lost VPS doesn't mean lost data.
            Point <code className="px-1 rounded bg-white/10 text-[11px]">MONGO_URL</code> at the backup URI on a fresh VPS and you're live again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={testConnection}
            disabled={pinging}
            variant="outline"
            className="border-white/10"
            data-testid="backup-test-btn"
          >
            <Activity className={`w-4 h-4 mr-2 ${pinging ? 'animate-pulse' : ''}`} />
            {pinging ? 'Pinging…' : 'Test Connection'}
          </Button>
          <Button
            onClick={runNow}
            disabled={running || isRunningLive || !state.configured}
            className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold"
            data-testid="backup-run-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {isRunningLive ? 'Running…' : 'Run Backup Now'}
          </Button>
        </div>
      </div>

      {/* STATUS CARDS */}
      <div className="grid md:grid-cols-4 gap-3">
        <KpiCard
          label="Connection"
          value={state.configured ? (isHealthy ? 'Healthy' : 'Degraded') : 'Not configured'}
          color={state.configured ? (isHealthy ? '#22c55e' : '#f59e0b') : '#6b7280'}
          icon={state.configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          testid="backup-kpi-connection"
          sub={state.target_uri_host || 'no host'}
        />
        <KpiCard
          label="Last Run"
          value={state.last_run_at ? relTime(state.last_run_at) : 'Never'}
          color={isHealthy ? '#22c55e' : '#f59e0b'}
          icon={<Clock className="w-4 h-4" />}
          testid="backup-kpi-last-run"
          sub={state.last_run_at ? new Date(state.last_run_at).toLocaleString() : '—'}
        />
        <KpiCard
          label="Last Sync Docs"
          value={(state.last_docs_copied || 0).toLocaleString()}
          color="#6366f1"
          icon={<Database className="w-4 h-4" />}
          testid="backup-kpi-docs"
          sub={`${state.last_duration_sec || 0}s duration`}
        />
        <KpiCard
          label="Interval"
          value={`${state.interval_hours || 6}h`}
          color="#D4AF37"
          icon={<RefreshCw className="w-4 h-4" />}
          testid="backup-kpi-interval"
          sub={`→ ${state.target_db_name || '—'}`}
        />
      </div>

      {/* Current status line */}
      <div
        className="rounded-2xl p-4 flex items-center gap-3"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${isHealthy ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}
        data-testid="backup-status-line"
      >
        {isRunningLive ? (
          <RefreshCw className="w-5 h-5 animate-spin text-sky-400" />
        ) : isHealthy ? (
          <CheckCircle2 className="w-5 h-5 text-green-400" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-400" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-semibold ${statusColor}`}>
            {isRunningLive
              ? 'Backup in progress — syncing collections to Atlas…'
              : `Status: ${state.last_status || 'unknown'}`}
          </p>
          {state.last_error && (
            <p className="text-xs text-red-400 mt-0.5">Last error: {state.last_error}</p>
          )}
          {!state.configured && (
            <p className="text-xs text-gray-400 mt-0.5">
              Set <code>MONGO_BACKUP_URL</code> in <code>backend/.env</code> to enable backups.
            </p>
          )}
        </div>
      </div>

      {/* Per-collection table */}
      {state.collections && Object.keys(state.collections).length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-3 border border-white/5"
          style={{ background: 'rgba(255,255,255,0.02)' }}
          data-testid="backup-collections-section"
        >
          <div className="flex items-start gap-3">
            <Database className="w-5 h-5 text-indigo-400 mt-0.5" />
            <div>
              <h2 className="font-bold text-white text-base">Per-Collection Snapshot</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                From the most recent sync — source vs target counts must match for a healthy collection.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 px-2">Collection</th>
                  <th className="text-right py-2 px-2">Source</th>
                  <th className="text-right py-2 px-2">Target</th>
                  <th className="text-right py-2 px-2">Copied</th>
                  <th className="text-right py-2 px-2">Duration</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(state.collections)
                  .sort(([, a], [, b]) => (b.source_count || 0) - (a.source_count || 0))
                  .map(([name, row]) => {
                    const err = row.error;
                    const mismatch = !err && row.source_count !== undefined
                      && row.target_count !== undefined
                      && row.source_count !== row.target_count;
                    return (
                      <tr
                        key={name}
                        className="border-b border-white/5 hover:bg-white/5"
                        data-testid={`backup-row-${name}`}
                      >
                        <td className="py-2 px-2 font-mono text-xs text-white">{name}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">
                          {row.source_count != null ? row.source_count.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">
                          {row.target_count != null ? row.target_count.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-indigo-300">
                          {(row.copied || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-400">
                          {row.duration_sec != null ? `${row.duration_sec}s` : '—'}
                        </td>
                        <td className="py-2 px-2">
                          {err ? (
                            <span className="text-[11px] text-red-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {err.slice(0, 50)}
                            </span>
                          ) : mismatch ? (
                            <span className="text-[11px] text-amber-400">drift</span>
                          ) : (
                            <span className="text-[11px] text-green-400">synced</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DR playbook */}
      <div
        className="rounded-xl p-4 text-sm text-gray-300 leading-relaxed"
        style={{
          background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.05))',
          border: '1px solid rgba(56,189,248,0.25)',
        }}
        data-testid="backup-playbook"
      >
        <p className="font-bold text-white mb-2">🛟 Disaster Recovery Playbook</p>
        <ol className="list-decimal ml-5 space-y-1 text-xs">
          <li>Clone the Vegas Lobby repo on a new VPS.</li>
          <li>In <code className="text-[11px] px-1 rounded bg-white/10">backend/.env</code>, set <code>MONGO_URL</code> to the Atlas URI currently in <code>MONGO_BACKUP_URL</code>.</li>
          <li>Set <code>DB_NAME</code> to <code className="text-[11px] px-1 rounded bg-white/10">{state.target_db_name || 'vegas_lobby_backup'}</code> (the backup DB).</li>
          <li>Run <code>sudo supervisorctl restart all</code>. Every user, wallet, transaction is already there — players log in with their existing creds and resume.</li>
          <li>Once stable, flip the new VPS's local Mongo + Atlas roles so the new node becomes the primary writer and Atlas resumes as pure backup.</li>
        </ol>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, icon, sub, testid }) {
  return (
    <div
      className="rounded-xl p-3 border"
      style={{ background: '#131822', borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-lg font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function relTime(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return '—'; }
}
